<script>
  import {onMount, createEventDispatcher, onDestroy, tick} from 'svelte'
  import gsap from 'gsap'
  import ScrollTrigger from 'gsap/ScrollTrigger'
  import Diagram from '../charts/FragmentedAudiences.svelte'
  import AnimatedTitle from '../components/AnimatedTitle.svelte'
  import AnimatedLine from '../components/AnimatedLine.svelte'
  import ViewSource from '../components/ViewSource.svelte'
  import AnimatedBlock from '../components/AnimatedBlock.svelte'

  let bg, buttons, linearBtn, digitalBtn, st, fadeHeaderBgTL, setupTimeout, part6b;

  const mobileImage = 'https://res.cloudinary.com/tanka-design/image/upload/q_auto/v1616019297/VerizonMediaATV/intro-img2-mobile_jfltf6.jpg'
  const desktopImage = 'https://res.cloudinary.com/tanka-design/image/upload/q_auto/v1616019297/VerizonMediaATV/intro-footer-desktop_ypalju.jpg'

  const dispatch = createEventDispatcher()

  onMount(() => {
    setupHeaderTransition()

    gsap.fromTo(bg, {opacity: 0, scale: 1.25},
      {
        opacity: 0.5,
        scale: 1,
        ease: 'power1.out',
        duration: 1.5,
        scrollTrigger: {
          trigger: bg,
          start: 'top 70%',
          toggleActions: 'play play play pause',
        }
      })
      if(buttons) {
        
        gsap.fromTo(buttons.querySelectorAll('button'), {
          opacity: 0,
          y: 40
        }, {
          opacity: 1,
          y: 0,
          ease: 'power2.out',
          duration: 0.75,
          stagger: 0.1,
          scrollTrigger: {
            trigger: buttons,
            start: 'bottom bottom-=20',
            toggleActions: 'play play play pause'
          }
        })
      }
    })

    function setupHeaderTransition () {
    
      fadeHeaderBgTL = gsap.timeline()
      fadeHeaderBgTL.to('header', {backgroundColor: 'rgba(0,0,0,0)', borderBottomColor: 'rgba(0,0,0,0)', ease: 'none', duration: 0.35}, 0)
      st = ScrollTrigger.create({
        trigger: part6b,
        start: 'top top+=80',
        end: '+=50',
        animation: fadeHeaderBgTL,
        scrub: 0.3,
      })

    }
    onDestroy(() => {
      killHeaderTransition()
    })
    
    function killHeaderTransition () {
      fadeHeaderBgTL?.pause().kill()
      st?.kill()
      fadeHeaderBgTL = null
      st = null
      if(setupTimeout) clearTimeout(setupTimeout)
    }

    function clickLinear () {
      killHeaderTransition()
      dispatch('path', {name: 'linear'})
    }
    
    function clickDigital () {
      killHeaderTransition()
      dispatch('path', {name: 'digital'})
    }

</script>
<section class="part6">
  <div class="rgrid-md-3-4">
    <div class="col">
      <AnimatedTitle class="medium" style="maskup"><span class="accent">When</span> <span class="accent">you</span> <span class="accent">have</span> <span class="accent">fragmented</span> <span class="accent">audiences,</span> it’s much harder to control reach and frequency.</AnimatedTitle>
      <AnimatedBlock useChildren={true} delay={0.2}>
        <p class="subtitle">Today on average, almost half of the 18-49 demo watching linear TV is either overexposed to your message or not exposed to it at all.</p>
        <ViewSource id="half_18_49_exposed"><a target="_blank" rel="noopener" href="https://www.simulmedia.com/assets/media/TV-Advertising-Reach-Frequency-Imbalance-Guide-Simulmedia.pdf">SimulMedia</a></ViewSource>
      </AnimatedBlock>
    </div>
  </div>
  <div class="diagram">
    <Diagram />
  </div>
</section>
<section class="part6b super-wide" bind:this={part6b}>
  <div class="content">
    <div class="title-wrap">
      <div class="rgrid-md-12-col">
        <div class="col">
          <AnimatedLine size={5} color="white" />
          <AnimatedTitle class="medium" style="maskup" speed={2}>The TV media landscape may be fragmented, but that doesn’t mean your TV strategy has to be.</AnimatedTitle>
          <AnimatedTitle size={2} triggerStart="top 80%" class="subtitle">Let’s look at this from your perspective:</AnimatedTitle>
          <div class="buttons" bind:this={buttons}>
            <button type="button" class="linear" bind:this={linearBtn} on:click|preventDefault={clickLinear}>I’m a Linear Buyer</button>
            <button type="button" class="digital" bind:this={digitalBtn} on:click|preventDefault={clickDigital}>I’m a Digital Buyer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="bg">
    <picture>
      <source srcset={mobileImage} media="(max-width: 767px)" />
      <source srcset={desktopImage} media="(min-width: 768px)" />
      <img bind:this={bg} src={mobileImage} alt="Family watching TV" />
    </picture>
  </div>
</section>
<style>
  .diagram {
    margin-top: 40px;
  }
  .part6 :global(.subtitle) {
    margin-bottom: 0;
  }
  .part6 :global(.view-source-wrap) {
    margin-top: 0.5rem;
  }
  .part6b {
    height: 900px;
    padding-bottom: 0;
  }
  .part6b .content {
    height: 100%;
    display: flex;
    align-items: flex-end;
    z-index: 2;
  }
  .part6b .title-wrap {
    padding-bottom: 6rem;
    width: 100%;
  }
  .part6b :global(.title) {
    margin-bottom: 1.125rem;
  }
  .part6b :global(.subtitle) {
    margin-top: 2.25rem;
    margin-bottom: 2rem;
  }
  .bg {
    position: absolute;
    top: 0;
    height: 100%;
    overflow: hidden;
    left: 0;
    right: 0;
  }
  .bg picture {
    position: absolute;
    top: 0;
    left: 0;
  }
  .bg img,
  .bg picture {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .buttons {
    display: flex;
  }
  .buttons button:not(.custom) {
    white-space: nowrap;
    flex: 1;
  }
  .buttons .digital {
    margin-left: 10px
  }
  
  .part6b :global(.hline) {
    margin-bottom: 0.75rem;
  }
  @media(max-width: 767px) {
    .buttons button:not(.custom) {
      padding-left: 0;
      padding-right: 0;
    }
  }
  @media(min-width: 768px) {
    .part6b .col {
      grid-column: 4 / span 9;
    }
    .part6b {
      height: 1000px;
    }
    .part6b .buttons {
      width: min-content;
    }
  }
  @media(max-height: 600px) {
    .part6b {
      height: 740px;
    }
    .part6b .title-wrap {
      padding-bottom: 4.5rem;
    }
  }
</style>